import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';
import { translations } from './translations';
import { en, awaitTopbar, uuidv4 } from '@/globals/helpers';
import type { PnLanguages, PnSteps, PnStepProps } from '@/globals/types';

/**
 * The `pn-progress-stepper` is used to visualize the users progress through a flow (checkout/survey, etc...).
 **/
@Component({
  tag: 'pn-progress-stepper',
  styleUrl: 'pn-progress-stepper.scss',
})
export class PnProgressStepper {
  id: string = `pn-progress-stepper-${uuidv4()}`;

  idLabel: string = `${this.id}-label`;
  idBar: string = `${this.id}-bar`;

  minStep: 1 = 1;
  maxStep: 7 = 7;

  @Element() hostElement: HTMLElement;

  /** Percentage calculated with currentStep / totalSteps. */
  @State() progressValue: number = 0;

  /** Set custom label for the stepper, recommended default label is "Step". */
  @Prop({ mutable: true }) label?: string;
  /** Set total amount of steps, maximum `7` is allowed. */
  @Prop({ mutable: true }) totalSteps: PnSteps = this.maxStep;
  /** The current step of the progress stepper. */
  @Prop({ mutable: true }) currentStep: PnSteps = this.minStep;
  /** You can add a custom optional string to the label if you need a name for the current step. */
  @Prop() stepName?: string;
  /** Set a custom HTML id on the progress stepper element to associate progress bar with label. */
  @Prop() progressStepperId?: string = '';
  /**
   * Set the language manually, only use this prop if the pnTopbar is not loaded.
   * Will not overwrite the `label` prop if used.
   **/
  @Prop() language?: PnLanguages = null;

  @Watch('currentStep')
  validateCurrentStep(step: PnSteps) {
    this.validate(step, 'currentStep');
  }

  @Watch('totalSteps')
  validateTotalSteps(step: PnSteps) {
    this.validate(step, 'totalSteps');
  }

  @Watch('language')
  watchLanguage() {
    if (this.hasCustomLabel()) return;
    this.label = translations.STEP[this.language || en];
  }

  @Watch('progressStepperId')
  watchId() {
    if (this.progressStepperId) {
      this.idLabel = `${this.progressStepperId}-label`;
      this.idBar = `${this.progressStepperId}-bar`;
    } else {
      this.idLabel = `${this.id}-label`;
      this.idBar = `${this.id}-bar`;
    }
  }

  async componentWillLoad() {
    this.watchLanguage();
    this.watchId();

    this.validate(this.currentStep, 'currentStep');
    this.validate(this.totalSteps, 'totalSteps');

    if (this.language) return;
    await awaitTopbar(this.hostElement);
  }

  componentWillRender() {
    this.calculateProgress();
  }

  hasCustomLabel(): boolean {
    if (this.label === undefined) return false;
    return !Object.keys(translations.STEP).find(step => translations.STEP[step] === this.label);
  }

  validate(step: PnSteps, prop: PnStepProps) {
    const componentName = this.hostElement.localName;
    if (step < this.minStep) {
      console.warn(
        `${componentName}: The ${prop} ${step} is below the minimum allowed steps of ${this.minStep}. ${prop} will default to ${this.minStep}.`,
      );
      this[prop] = this.minStep;
    }
    if (prop === 'currentStep' && step > this.totalSteps) {
      console.warn(
        `${componentName}: The ${prop} ${step} is above totalSteps ${this.totalSteps}, ${prop} will default to ${this.totalSteps}.`,
      );
      this[prop] = this.totalSteps;
    }
    if (step > this.maxStep) {
      console.warn(
        `${componentName}: The ${prop} ${step} is above the maximum allowed steps of ${this.maxStep}. ${prop} will default to ${this.maxStep}.`,
      );
      this[prop] = this.maxStep;
    }
  }

  calculateProgress() {
    try {
      this.progressValue = Math.floor((this.currentStep / this.totalSteps) * 100);
      this.hostElement.style.setProperty('--progress-value', `${this.progressValue}%`);
    } catch (error) {
      console.error(`${this.hostElement.localName}:`, error.message);
    }
  }

  render() {
    return (
      <Host>
        <p id={this.idLabel} class="pn-progress-stepper-label">
          <span>
            {this.label} {this.currentStep}/{this.totalSteps}
          </span>
          {this.stepName && <span> - {this.stepName}</span>}
        </p>
        <div
          class="pn-progress-stepper"
          role="progressbar"
          aria-labelledby={this.idLabel}
          aria-valuenow={this.progressValue}
          id={this.idBar}
          data-full={this.currentStep === this.totalSteps}
        >
          <div class="pn-progress-stepper-value"></div>
        </div>
      </Host>
    );
  }
}
